import {
  apiRequest, getBackendRefreshToken, getBackendToken, setBackendSession,
} from '../api/client';

const REGISTRY_KEY = 'amposter:linked-backend-accounts:v1';
const PENDING_KEY = 'amposter:pending-add-account:v1';

export const getBackendAccounts = () => {
  try {
    const value = JSON.parse(localStorage.getItem(REGISTRY_KEY) || '[]');
    return Array.isArray(value) ? value.filter((account) => account?.id && (account?.token || account?.refreshToken)) : [];
  } catch (_) { return []; }
};

const save = (accounts) => localStorage.setItem(REGISTRY_KEY, JSON.stringify(accounts));

const accountMeta = (profile, token, refreshToken, existing = {}) => ({
  ...existing,
  id: profile?.id || profile?.uid,
  name: profile?.name || profile?.displayName || profile?.userHandle || existing.name || '',
  tag: profile?.tag || (profile?.userHandle ? `@${profile.userHandle}` : existing.tag || ''),
  img: profile?.img || profile?.avatarUrl || existing.img || '',
  token,
  refreshToken: refreshToken || existing.refreshToken || null,
  addedAt: existing.addedAt || Date.now(),
});

export const registerCurrentBackendAccount = (profile, authenticatedUserId = profile?.id || profile?.uid) => {
  const token = getBackendToken();
  const refreshToken = getBackendRefreshToken();
  const id = profile?.id || profile?.uid;
  if (!id || !token || id !== authenticatedUserId || isAddingBackendAccount()) return getBackendAccounts();
  const accounts = getBackendAccounts();
  const index = accounts.findIndex((account) => account.id === id);
  const next = accountMeta(profile, token, refreshToken, index >= 0 ? accounts[index] : {});
  if (index >= 0) accounts[index] = next;
  else accounts.push(next);
  save(accounts);
  return accounts;
};

export const beginAddBackendAccount = (profile, suppliedToken = null, suppliedRefreshToken = null) => {
  const token = suppliedToken || getBackendToken();
  const refreshToken = suppliedRefreshToken || getBackendRefreshToken();
  if (suppliedToken) {
    const accounts = getBackendAccounts();
    const index = accounts.findIndex((account) => account.id === profile?.id);
    const next = accountMeta(profile, token, refreshToken, index >= 0 ? accounts[index] : {});
    if (index >= 0) accounts[index] = next; else accounts.push(next);
    save(accounts);
  } else registerCurrentBackendAccount(profile);
  localStorage.setItem(PENDING_KEY, JSON.stringify({
    originId: profile?.id || null, originToken: token || null, originRefreshToken: refreshToken || null,
  }));
};

export const isAddingBackendAccount = () => Boolean(localStorage.getItem(PENDING_KEY));

export const completeAddBackendAccount = (authResponse) => {
  const user = authResponse?.user;
  const token = authResponse?.accessToken || getBackendToken();
  const refreshToken = authResponse?.refreshToken || getBackendRefreshToken();
  if (!user?.id || !token) throw new Error('The added account session could not be saved.');
  const accounts = getBackendAccounts();
  const index = accounts.findIndex((account) => account.id === user.id);
  const next = accountMeta(user, token, refreshToken, index >= 0 ? accounts[index] : {});
  if (index >= 0) accounts[index] = next;
  else accounts.push(next);
  save(accounts);
  localStorage.removeItem(PENDING_KEY);
  setBackendSession(token, refreshToken);
  return next;
};

export const cancelAddBackendAccount = () => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
    if (pending?.originToken || pending?.originRefreshToken) {
      setBackendSession(pending.originToken, pending.originRefreshToken);
    }
  } catch (_) { /* keep the current authenticated session */ }
  localStorage.removeItem(PENDING_KEY);
};

export const switchToBackendAccount = (accountId, options) => {
  const account = getBackendAccounts().find((item) => item.id === accountId);
  if (!account) throw new Error('This account is no longer linked on this device.');
  if (options) setBackendSession(account.token, account.refreshToken, options);
  else setBackendSession(account.token, account.refreshToken);
  return account;
};

// Validate the selected credentials in isolation: never use the current account's
// cookie or refresh token to "repair" a different linked account.
export const prepareBackendAccount = async (accountId) => {
  const account = getBackendAccounts().find(item => item.id === accountId);
  if (!account) throw new Error('ACCOUNT_REAUTH_REQUIRED');
  const isolated = { skipAuth: true, retryAuth: false, notifyAuthFailure: false, credentials: 'omit', deduplicate: false };
  let user;
  if (account.token) {
    try { user = await apiRequest('/api/v1/auth/me', { ...isolated, headers: { Authorization: `Bearer ${account.token}` } }); }
    catch (error) { if (error.status !== 401) throw error; }
  }
  let token = account.token, refreshToken = account.refreshToken;
  if (!user || user.id !== accountId) {
    if (!refreshToken) { removeBackendAccount(accountId); throw new Error('ACCOUNT_REAUTH_REQUIRED'); }
    let response;
    try { response = await apiRequest('/api/v1/auth/refresh', { ...isolated, method: 'POST', body: { refreshToken } }); }
    catch (error) {
      // A confirmed revoked session should not remain as a broken switch target.
      // Network failures and maintenance never remove a valid saved account.
      if (error.status === 401) { removeBackendAccount(accountId); throw new Error('ACCOUNT_REAUTH_REQUIRED'); }
      throw error;
    }
    token = response.accessToken; refreshToken = response.refreshToken;
    if (!token || !refreshToken) throw new Error('ACCOUNT_REAUTH_REQUIRED');
    user = await apiRequest('/api/v1/auth/me', { ...isolated, headers: { Authorization: `Bearer ${token}` } });
  }
  if (user?.id !== accountId) throw new Error('ACCOUNT_REAUTH_REQUIRED');
  const accounts = getBackendAccounts();
  save(accounts.map(item => item.id === accountId ? accountMeta(user, token, refreshToken, item) : item));
  return { user, accessToken: token, refreshToken };
};

export const removeBackendAccount = (accountId) => {
  const remaining = getBackendAccounts().filter((account) => account.id !== accountId);
  save(remaining);
  try {
    if (JSON.parse(localStorage.getItem(PENDING_KEY) || 'null')?.originId === accountId) localStorage.removeItem(PENDING_KEY);
  } catch (_) { localStorage.removeItem(PENDING_KEY); }
  return remaining;
};
